import { formatQuantityOrDash } from '@/lib/format/number';
import { unitSymbol } from './units';
import { UNIT_TYPES } from '@/config/unitTypes.constants';

// A row's quantities in each unit; the unit-type cell picks which one shows.

/**
 * One unit's slice of a row: its unit symbol plus each amount pre-rendered.
 *
 * @param unit    the raw unit from the server (mapped through unitSymbol)
 * @param amounts view-model field name → the amount in that unit
 */
export const quantitiesInUnit = (unit, amounts) => {
  const symbol = unitSymbol(unit);
  return {
    unit: symbol,
    ...Object.fromEntries(
      Object.entries(amounts).map(([field, amount]) => [
        field,
        formatQuantityOrDash(amount, symbol),
      ]),
    ),
  };
};

/**
 * `byUnit` for a row, built from one `amounts` object per unit type.
 *
 * @param unitsByType unitType → { unit, amounts }
 */
export const buildQuantitiesByUnit = (unitsByType) =>
  Object.fromEntries(
    Object.entries(unitsByType)
      // A unit the row has no value for is omitted, so nothing offers or shows it.
      .filter(([, { unit }]) => Boolean(unit))
      .map(([unitType, { unit, amounts }]) => [unitType, quantitiesInUnit(unit, amounts)]),
  );

// The unit type a row starts on — the declaration unit, else the first one it actually carries.
export const defaultUnitType = (byUnit) =>
  byUnit?.[UNIT_TYPES.declaration]
    ? UNIT_TYPES.declaration
    : (Object.keys(byUnit ?? {})[0] ?? null);

// Same shape as `buildQuantitiesByUnit`, but unformatted — for calculations, never display.
export const buildRawQuantitiesByUnit = (unitsByType) =>
  Object.fromEntries(
    Object.entries(unitsByType)
      .filter(([, { unit }]) => Boolean(unit))
      .map(([unitType, { amounts }]) => [unitType, amounts]),
  );

/** The unit symbol a row is currently showing; every row type carries `byUnit`. */
export const rowUnitSymbol = (row, unitType) => row?.byUnit?.[unitType]?.unit ?? '';
